import { CommonModule } from '@angular/common';
import { Component, computed, inject, output } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { IonIcon } from '@ionic/angular';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { combineLatest } from 'rxjs';
import { filter, map, shareReplay, startWith, switchMap } from 'rxjs/operators';
import { ILanguage } from 'src/app/models/language.model';
import { Message } from 'src/app/models/message.model';
import { MessagesService } from 'src/app/services/messages.service';
import { UserSessionService } from 'src/app/services/user-session.service';
import { WorkerMessagesService } from 'src/app/services/worker-messages.service';
import { notificationKeys } from 'src/app/utility/constants';
import { sortAnyByDate } from 'src/app/utility/sort';
import { ticketCreated } from 'src/app/utility/ticket';
import { NotificationPopoverItem } from './notification-popover.model';

@Component({
  selector: 'app-notification-popover',
  imports: [
    CommonModule,
    IonIcon,
    TranslatePipe,
  ],
  templateUrl: './notification-popover.component.html',
  styleUrl: './notification-popover.component.scss',
})
export class NotificationPopoverComponent {
  private messagesService = inject(MessagesService);
  private translate = inject(TranslateService);
  private usersService = inject(UserSessionService);
  private workerMessagesService = inject(WorkerMessagesService);

  private lang$ = this.translate.onLangChange.pipe(
    map((event) => event.lang),
    startWith(this.usersService.currentUserS()?.lang ?? this.translate.getCurrentLang() ?? 'fi'),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  private itemsWithMessage = toSignal(
    this.usersService.getCurrentUser().pipe(
      filter((user) => user != null),
      switchMap((user) => combineLatest([
        this.messagesService.getList({ user: user.guid }),
        this.lang$,
      ])),
      map(([messages, lang]) => messages
        .sort((a, b) => sortAnyByDate(a, b, 'created'))
        .reverse()
        .filter((message) => !message.deleted && message.body !== ticketCreated)
        .map((message) => this.toNotificationItem(message, lang))),
      shareReplay({ bufferSize: 1, refCount: true }),
    ),
    { initialValue: [] as (NotificationPopoverItem & { message: Message })[] },
  );

  readonly items = computed<NotificationPopoverItem[]>(() =>
    this.itemsWithMessage().map(({ message: _message, ...item }) => item));

  readonly notificationSelected = output<NotificationPopoverItem>();
  readonly closeRequested = output();

  readonly resolvedUnreadCount = computed(() => this.items().filter((item) => item.unread).length);

  readonly hasUnreadBadge = computed(() => this.resolvedUnreadCount() > 0);

  onSelect(item: NotificationPopoverItem): void {
    const selected = this.itemsWithMessage().find((notification) => notification.id === item.id);
    if (selected) {
      const message = selected.message;
      const data = message.data ?? '';
      if (data.startsWith(notificationKeys.ticket)) {
        const [ticketGuid, roomTicketGuid] = data.substring(data.indexOf('-') + 1).split('$');
        this.workerMessagesService.onTicket(ticketGuid, roomTicketGuid, message);
      } else if (data.startsWith(notificationKeys.reservation)) {
        const [, reservationGuid, siteGuid] = data.split('-');
        this.workerMessagesService.onReservation(siteGuid, reservationGuid, message);
      }
    }
    this.notificationSelected.emit(item);
    this.closeRequested.emit();
  }

  onClose(): void {
    this.closeRequested.emit();
  }

  private toNotificationItem(message: Message, lang: string): NotificationPopoverItem & { message: Message } {
    const localizedTitle = this.resolveMessageText(message.title, lang);
    const localizedBody = this.resolveMessageText(message.body, lang);
    const data = message.data ?? '';
    const isTicket = data.startsWith(notificationKeys.ticket);
    const isReservation = data.startsWith(notificationKeys.reservation);
    const type: NotificationPopoverItem['type'] = isTicket
      ? 'task'
      : isReservation
        ? 'reservation'
        : 'message';

    return {
      id: message.guid,
      type,
      title: localizedTitle,
      subtitle: isTicket ? localizedBody : message.sender?.name ?? localizedBody,
      timeLabel: this.formatNotificationTime(message.created, lang),
      unread: !message.read,
      targetType: type,
      targetId: message.guid,
      message,
    };
  }

  private resolveMessageText(value: string | ILanguage, lang: string) {
    if (typeof value === 'string') {
      const translated = this.translate.instant(value);
      return typeof translated === 'string' ? translated : value;
    }
    return value?.[lang as keyof ILanguage] ?? value?.fi ?? value?.en ?? '';
  }

  private formatNotificationTime(value: unknown, lang: string) {
    const date = value instanceof Date ? value : new Date(value as string | number | Date);
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
      return '-';
    }
    const locale = lang === 'fi' ? 'fi-FI' : undefined;
    return new Intl.DateTimeFormat(locale, {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  }

  iconName(item: NotificationPopoverItem): string {
    switch (item.type) {
      case 'task':
        return 'checkmark-circle-outline';
      case 'message':
        return 'chatbubble-ellipses-outline';
      case 'reservation':
        return 'calendar-outline';
      case 'warning':
        return 'alert-circle-outline';
      default:
        return 'notifications-outline';
    }
  }
}
